import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {mobileOnlyLinaria} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {CampaignSaleBadge} from './badges/CampaignSaleBadge';
import {
  Badge,
  BadgeContainer,
  BadgeWithBorder,
  BadgeWithBorderRadius,
} from './badges/GenericBadges';
import {NoteBadge} from './badges/NoteBadge';
import {PersonalizedBadge} from './badges/PersonalisedBadge';
import {SaleBadge} from './badges/SaleBadge';
import {UpcyclingBadge} from './badges/UpcyclingBadge';
import {YourSizeBadge} from './badges/YourSizeBadge';
import {BadgeListProps} from './types';

const ListWrapper = styled.div`
  & ${BadgeContainer} {
    font-size: ${FONT_SIZES.px10};
    font-weight: ${FONT_WEIGHTS.bold};

    ${mobileOnlyLinaria(`font-weight: ${FONT_WEIGHTS.mediumBold};`)}
  }
`;

export const BadgeListSmall: React.FC<
  BadgeListProps & {
    className?: string;
    testId?: string;
  }
> = ({className, testId, ...badgeListProps}) => {
  return (
    <ListWrapper className={className} data-testid={testId}>
      <BadgeList {...badgeListProps} />
    </ListWrapper>
  );
};

export function BadgeList(props: BadgeListProps) {
  const {t} = useTranslation();
  const {
    disableRoundedCorners = true,
    showYourSizeInfoIcon = true,
    entries,
  } = props;

  return (
    <>
      {entries.map((badge, index) => {
        switch (badge.kind) {
          case 'GENERIC': {
            return (
              <Badge
                key={index}
                dataTestId={`badge-${badge.kind}`}
                disableRoundedCorners={disableRoundedCorners}
                text={badge.label}
                textColor={badge.textColor}
                backgroundColor={badge.backgroundColor}
                increaseRightSideBorderRadius={
                  props.increaseRightSideBorderRadius
                }
              />
            );
          }

          case 'GENERIC_WITH_BORDER': {
            return (
              <BadgeWithBorder
                key={index}
                dataTestId={`badge-${badge.kind}`}
                disableRoundedCorners={disableRoundedCorners}
                text={badge.label}
                increaseRightSideBorderRadius={
                  props.increaseRightSideBorderRadius
                }
              />
            );
          }

          case 'GENERIC_WITH_BORDER_RADIUS': {
            const isTooltipAvailable =
              props.onClick &&
              Boolean(badge.tooltip) &&
              badge.tooltip?.headline &&
              badge.tooltip.body;

            const svgIcon: React.ComponentProps<typeof Badge>['svgIcon'] =
              isTooltipAvailable
                ? {
                    iconName: GenericIconName.Info,
                    iconSize: 'XSS',
                    showToTheRight: true,
                  }
                : undefined;

            return (
              <BadgeWithBorderRadius
                key={index}
                dataTestId={`badge-${badge.kind}`}
                disableRoundedCorners={disableRoundedCorners}
                text={badge.label}
                onClick={e =>
                  isTooltipAvailable &&
                  props.onClick?.(
                    e,
                    badge?.tooltip?.headline,
                    badge?.tooltip?.body,
                  )
                }
                svgIcon={svgIcon}
                backgroundColor={badge.backgroundColor}
                increaseRightSideBorderRadius={
                  props.increaseRightSideBorderRadius
                }
              />
            );
          }

          case 'PERSONALIZED': {
            return (
              <PersonalizedBadge
                key={index}
                dataTestId={`badge-${badge.kind}`}
                disableRoundedCorners={disableRoundedCorners}
                type={badge.variation}
                increaseRightSideBorderRadius={
                  props.increaseRightSideBorderRadius
                }
              />
            );
          }

          case 'SALE': {
            return (
              <SaleBadge
                key={index}
                dataTestId={`badge-${badge.kind}`}
                disableRoundedCorners={disableRoundedCorners}
                text={badge.label}
                increaseRightSideBorderRadius={
                  props.increaseRightSideBorderRadius
                }
              />
            );
          }

          case 'CAMPAIGN_SALE': {
            return (
              <CampaignSaleBadge
                key={index}
                dataTestId={`badge-${badge.kind}`}
                backgroundColor={badge.color}
                disableRoundedCorners={disableRoundedCorners}
                text={badge.label}
                increaseRightSideBorderRadius={
                  props.increaseRightSideBorderRadius
                }
              />
            );
          }

          case 'IN_BASKET': {
            return (
              <NoteBadge
                key={index}
                dataTestId={`badge-${badge.kind}`}
                disableRoundedCorners={disableRoundedCorners}
                text={t('In the basket')}
                increaseRightSideBorderRadius={
                  props.increaseRightSideBorderRadius
                }
              />
            );
          }

          case 'BEST_PRICE': {
            return (
              <NoteBadge
                key={index}
                dataTestId={`badge-${badge.kind}`}
                disableRoundedCorners={disableRoundedCorners}
                text={badge.label}
                increaseRightSideBorderRadius={
                  props.increaseRightSideBorderRadius
                }
              />
            );
          }

          case 'UPCYCLING': {
            return (
              <UpcyclingBadge
                key={index}
                dataTestId={`badge-${badge.kind}`}
                text={badge.label}
              />
            );
          }

          case 'YOUR_SIZE': {
            return (
              <YourSizeBadge
                key={index}
                data-testid={`badge-${badge.kind}`}
                showInfoIcon={showYourSizeInfoIcon}
              />
            );
          }

          case 'COUPON': {
            return (
              <Badge
                key={index}
                dataTestId={`badge-${badge.kind}`}
                disableRoundedCorners={disableRoundedCorners}
                increaseRightSideBorderRadius={
                  props.increaseRightSideBorderRadius
                }
                text={badge.label}
                textColor={badge.textColor}
                backgroundColor={badge.backgroundColor}
                svgIcon={{
                  iconName: GenericIconName.Coupon,
                  iconSize: 'XS',
                  fill: badge.textColor,
                }}
              />
            );
          }

          case 'COUPON_DISABLED': {
            return (
              <Badge
                key={index}
                dataTestId={`badge-${badge.kind}`}
                disableRoundedCorners={disableRoundedCorners}
                increaseRightSideBorderRadius={
                  props.increaseRightSideBorderRadius
                }
                text={badge.label}
                textColor={COLORS.White}
                backgroundColor={COLORS.Grey2}
                svgIcon={{
                  iconName: GenericIconName.Coupon,
                  iconSize: 'XS',
                  fill: COLORS.White,
                }}
              />
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
}
